import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Chip, Grid, ButtonGroup,
  CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody, TableFooter,
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  CheckCircle as FreshIcon,
  Warning as WatchIcon,
  Error as OverdueIcon,
} from '@mui/icons-material';
import DateFilterBar, { DEFAULT_RANGE } from '../components/DateFilterBar';
import { fetchPalletAging, exportExcel, todayStr } from '../utils/reportsApi';

const STATUS_COLORS = {
  'REPACK-OUT':  { bg: '#ffedd5', fg: '#9a3412' },
  'RE-PRINTING': { bg: '#fce7f3', fg: '#be185d' },
  'RE-PROCESS':  { bg: '#f3e8ff', fg: '#7e22ce' },
  'QC SAMPLE':   { bg: '#cffafe', fg: '#0e7490' },
};

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || { bg: '#f1f5f9', fg: '#475569' };
  return (
    <Chip label={status} size="small" sx={{
      bgcolor: c.bg, color: c.fg, fontWeight: 700, fontSize: 10.5,
      height: 22, '& .MuiChip-label': { px: 1.25 },
    }} />
  );
}

function AgeBadge({ days }) {
  let bg, fg;
  if (days <= 7)       { bg = '#dcfce7'; fg = '#15803d'; }
  else if (days <= 14) { bg = '#fef3c7'; fg = '#92400e'; }
  else                 { bg = '#fee2e2'; fg = '#991b1b'; }
  return (
    <Box component="span" sx={{
      display: 'inline-block', bgcolor: bg, color: fg,
      fontWeight: 700, fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
      px: 1, py: 0.25, borderRadius: 0.5,
    }}>
      {days}d
    </Box>
  );
}

function GoNoChip({ no }) {
  if (!no) return <span>—</span>;
  const prefix = no.split('-')[0];
  const colors = {
    RPK: { bg: '#f5edf7', fg: '#8E44AD' },
    RPT: { bg: '#fce7f3', fg: '#be185d' },
    RPS: { bg: '#f3e8ff', fg: '#7e22ce' },
    QC:  { bg: '#cffafe', fg: '#0e7490' },
    FCL: { bg: '#ede9fe', fg: '#5b21b6' },
  };
  const c = colors[prefix] || { bg: '#f1f5f9', fg: '#475569' };
  return (
    <Box component="span" sx={{
      fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 11.5,
      bgcolor: c.bg, color: c.fg, px: 0.875, py: 0.25, borderRadius: 0.5,
    }}>
      {no}
    </Box>
  );
}

function KpiCard({ label, value, subtitle, icon: Icon, color }) {
  return (
    <Paper variant="outlined" sx={{
      p: 1.75, borderLeft: `4px solid ${color}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, color: '#64748b', textTransform: 'uppercase', mb: 0.5 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', lineHeight: 1.1 }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', mt: 0.5 }}>{subtitle}</Typography>
          )}
        </Box>
        {Icon && <Icon sx={{ color, fontSize: 22, mt: 0.25 }} />}
      </Box>
    </Paper>
  );
}

export default function PalletAgingReport({ user, showToast }) {
  const [dateFilter, setDateFilter] = useState(DEFAULT_RANGE);
  const [statusFilter, setStatusFilter] = useState('all');
  const [agingFilter, setAgingFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const res = await fetchPalletAging(dateFilter.from, dateFilter.to);
    if (res.success) setData(res.data);
    else setError(res.error);
    setLoading(false);
  }, [dateFilter.from, dateFilter.to]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.pallets.filter(p => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (agingFilter === 'fresh'   && p.age_days > 7)                       return false;
      if (agingFilter === 'watch'   && (p.age_days <= 7 || p.age_days > 14)) return false;
      if (agingFilter === 'overdue' && p.age_days <= 14)                     return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = [p.pallet_id, p.gateout_no, p.product, p.lot_no, p.destination_dept]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [data, statusFilter, agingFilter, search]);

  const filteredSummary = useMemo(() => {
    let totalCartons = 0, totalKgs = 0;
    filtered.forEach(p => { totalCartons += p.cartons; totalKgs += p.total_kgs; });
    return { totalCartons, totalKgs };
  }, [filtered]);

  const handleExport = () => {
    const headers = ['Pallet ID', 'Status', 'Gate-Out No', 'Destination', 'Gate-Out Date',
                     'Age (days)', 'Product', 'Grade', 'Lot No', 'Cartons', 'Total KG', 'Total LB'];
    const rows = filtered.map(p => [
      p.pallet_id, p.status, p.gateout_no || '', p.destination_dept || '',
      p.gateout_date, p.age_days, p.product, p.grade, p.lot_no,
      p.cartons, p.total_kgs, p.total_pounds,
    ]);
    const summary = data?.summary || {};
    const summaryRows = [
      ['Date Range', `${dateFilter.from} to ${dateFilter.to}`],
      ['Status Filter', statusFilter === 'all' ? 'All' : statusFilter],
      ['Aging Filter', agingFilter === 'all' ? 'All' : agingFilter],
      ['Total (filtered)', filtered.length],
      ['Fresh (≤7d)', summary.fresh],
      ['Watch (8-14d)', summary.watch],
      ['Overdue (>14d)', summary.overdue],
      ['Total Cartons (filtered)', filteredSummary.totalCartons],
      ['Total KG (filtered)', filteredSummary.totalKgs.toFixed(2)],
    ];
    exportExcel({
      filename: `pallet_aging_${dateFilter.from}_to_${dateFilter.to}_${todayStr()}.xlsx`,
      sheets: [
        { name: 'Aging Pallets', headers, rows },
        { name: 'Summary', headers: ['Metric', 'Value'], rows: summaryRows },
      ],
    });
    showToast?.({ message: '📥 Excel exported', severity: 'success' });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
          ⏱ Pallet Aging Report
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
          All pallets currently in any "out" state · sorted by age · color-coded by aging bucket
        </Typography>
      </Box>

      <DateFilterBar
        value={dateFilter}
        onChange={setDateFilter}
        onExport={handleExport}
        exportLabel="Export Aging Report"
      />

      {/* KPI Cards */}
      {data && (
        <Grid container spacing={1.25} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="Total Out-State Pallets" value={data.summary.total}
                     subtitle={`${data.summary.total_cartons.toLocaleString()} ctn · ${Math.round(data.summary.total_kgs).toLocaleString()} KG`}
                     icon={ClockIcon} color="#1F618D" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="🟢 Fresh (0-7 days)" value={data.summary.fresh}
                     subtitle="on track" icon={FreshIcon} color="#16a34a" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="🟡 Watch (8-14 days)" value={data.summary.watch}
                     subtitle="approaching threshold" icon={WatchIcon} color="#ea580c" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard label="🔴 Overdue (>14 days)" value={data.summary.overdue}
                     subtitle="requires attention" icon={OverdueIcon} color="#dc2626" />
          </Grid>
        </Grid>
      )}

      {/* Sub-filters */}
      <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: '#64748b', textTransform: 'uppercase' }}>
          Status
        </Typography>
        <ButtonGroup size="small">
          {[
            { id: 'all', label: 'All' },
            { id: 'REPACK-OUT', label: 'Repack-Out' },
            { id: 'RE-PRINTING', label: 'Reprint-Out' },
            { id: 'RE-PROCESS', label: 'Reprocess-Out' },
            { id: 'QC SAMPLE', label: 'QC Sample' },
          ].map(s => (
            <Button key={s.id}
                    variant={statusFilter === s.id ? 'contained' : 'outlined'}
                    onClick={() => setStatusFilter(s.id)}
                    sx={{
                      fontSize: 11.5, textTransform: 'none', py: 0.25, px: 1.25,
                      ...(statusFilter === s.id ? { bgcolor: '#8E44AD', '&:hover': { bgcolor: '#7d3c98' } } : {}),
                    }}>
              {s.label}
            </Button>
          ))}
        </ButtonGroup>

        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: '#64748b', textTransform: 'uppercase', ml: 1.5 }}>
          Age
        </Typography>
        <ButtonGroup size="small">
          {[
            { id: 'all',     label: 'All ages' },
            { id: 'fresh',   label: '🟢 Fresh' },
            { id: 'watch',   label: '🟡 Watch' },
            { id: 'overdue', label: '🔴 Overdue' },
          ].map(a => (
            <Button key={a.id}
                    variant={agingFilter === a.id ? 'contained' : 'outlined'}
                    onClick={() => setAgingFilter(a.id)}
                    sx={{
                      fontSize: 11.5, textTransform: 'none', py: 0.25, px: 1.25,
                      ...(agingFilter === a.id ? { bgcolor: '#8E44AD', '&:hover': { bgcolor: '#7d3c98' } } : {}),
                    }}>
              {a.label}
            </Button>
          ))}
        </ButtonGroup>

        <TextField
          size="small"
          placeholder="Search pallet ID, GO No, product, lot..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220, ml: 'auto', '& input': { fontSize: 12 } }}
        />
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">{error}</Alert>
      )}

      {data && !loading && (
        <Paper variant="outlined">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Pallet ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Gate-Out No</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Gate-Out Date</TableCell>
                  <TableCell align="right">Age</TableCell>
                  <TableCell>Product · Grade</TableCell>
                  <TableCell>Lot</TableCell>
                  <TableCell align="right">Cartons</TableCell>
                  <TableCell align="right">Total KG</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: '#64748b' }}>
                      No pallets match the current filters.
                    </TableCell>
                  </TableRow>
                ) : filtered.map(p => (
                  <TableRow key={p.id} sx={{
                    bgcolor: p.age_days > 14 ? 'rgba(220, 38, 38, 0.04)' : 'transparent',
                    '&:hover': { bgcolor: '#f8fafc' },
                  }}>
                    <TableCell>
                      <strong style={{ fontFamily: 'JetBrains Mono, monospace', color: '#1F618D' }}>
                        {p.pallet_id}
                      </strong>
                    </TableCell>
                    <TableCell><StatusPill status={p.status} /></TableCell>
                    <TableCell><GoNoChip no={p.gateout_no} /></TableCell>
                    <TableCell sx={{ fontSize: 11.5, color: '#475569' }}>{p.destination_dept || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5 }}>{p.gateout_date || '—'}</TableCell>
                    <TableCell align="right"><AgeBadge days={p.age_days} /></TableCell>
                    <TableCell>
                      <strong>{p.product || '—'}</strong> · {p.grade || '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5 }}>{p.lot_no || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{p.cartons}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{p.total_kgs.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {filtered.length > 0 && (
                <TableFooter>
                  <TableRow sx={{ bgcolor: '#fafbfc', fontWeight: 700 }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 700, color: '#0f172a' }}>
                      Total ({filtered.length} pallets)
                    </TableCell>
                    <TableCell colSpan={3} />
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#0f172a' }}>
                      {filteredSummary.totalCartons}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#0f172a' }}>
                      {filteredSummary.totalKgs.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </Box>
        </Paper>
      )}

      <Typography sx={{ fontSize: 11.5, color: '#64748b', mt: 1 }}>
        💡 Aging buckets: <strong>Fresh</strong> = 0-7 days · <strong>Watch</strong> = 8-14 days · <strong>Overdue</strong> = &gt;14 days.
      </Typography>
    </Box>
  );
}
